import {
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  YouTube,
} from "@mui/icons-material";
import React from "react";

export type SocialMediaPlatform =
  | "youtube"
  | "linkedin"
  | "instagram"
  | "facebook"
  | "x"
  | "twitter";

export interface GroupedSocialMediaUrls {
  [platform: string]: string[];
}

/**
 * Detects the social media platform from a URL
 */
export const detectSocialMediaPlatform = (
  url: string
): SocialMediaPlatform | null => {
  if (!url) return null;

  const normalizedUrl = url.toLowerCase();

  // YouTube detection
  if (
    normalizedUrl.includes("youtube.com") ||
    normalizedUrl.includes("youtu.be")
  ) {
    return "youtube";
  }

  // LinkedIn detection
  if (normalizedUrl.includes("linkedin.com")) {
    return "linkedin";
  }

  // Instagram detection
  if (normalizedUrl.includes("instagram.com")) {
    return "instagram";
  }

  // Facebook detection
  if (
    normalizedUrl.includes("facebook.com") ||
    normalizedUrl.includes("fb.com")
  ) {
    return "facebook";
  }

  // X/Twitter detection
  if (
    normalizedUrl.includes("x.com") ||
    normalizedUrl.includes("twitter.com")
  ) {
    return "x";
  }

  return null;
};

/**
 * Groups URLs by social media platform
 */
export const groupUrlsByPlatform = (urls: string[]): GroupedSocialMediaUrls => {
  const grouped: GroupedSocialMediaUrls = {};

  urls.forEach((url) => {
    const platform = detectSocialMediaPlatform(url);
    if (platform) {
      if (!grouped[platform]) {
        grouped[platform] = [];
      }
      grouped[platform].push(url);
    }
  });

  return grouped;
};

/**
 * Gets the appropriate Material-UI icon component for a social media platform
 */
export const getSocialMediaIcon = (
  platform: SocialMediaPlatform
): React.ComponentType => {
  switch (platform) {
    case "youtube":
      return YouTube;
    case "linkedin":
      return LinkedIn;
    case "instagram":
      return Instagram;
    case "facebook":
      return Facebook;
    case "x":
    case "twitter":
      return Twitter;
    default:
      return Twitter; // Fallback icon
  }
};

/**
 * Gets platform-specific colors for hover effects
 */
export const getPlatformColor = (platform: SocialMediaPlatform): string => {
  switch (platform) {
    case "youtube":
      return "#FF0000";
    case "linkedin":
      return "#0077B5";
    case "instagram":
      return "#E4405F";
    case "facebook":
      return "#1877F2";
    case "x":
    case "twitter":
      return "#000000";
    default:
      return "#666666";
  }
};

/**
 * Gets human-readable platform names
 */
export const getPlatformDisplayName = (
  platform: SocialMediaPlatform
): string => {
  switch (platform) {
    case "youtube":
      return "YouTube";
    case "linkedin":
      return "LinkedIn";
    case "instagram":
      return "Instagram";
    case "facebook":
      return "Facebook";
    case "x":
      return "X";
    case "twitter":
      return "Twitter";
    default:
      return "Social Media";
  }
};

/**
 * Ensures URL has proper protocol for opening in browser
 */
export const normalizeUrl = (url: string): string => {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `https://${url}`;
  }
  return url;
};
